import React from 'react';
import R from 'ramda';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import Button from '@cotyorg/ccx-atoms/Button';
import Icon from '@cotyorg/ccx-atoms/Icon';
import { toggle as toggleAction } from '../modules';

const generateIconName = ({ isOpen, minusIconSrc, plusIconSrc }) => (isOpen ? minusIconSrc : plusIconSrc);

const generateAccordionButtonTextClassNames = ({ textExtraClass }) => classNames(
  'flex-container',
  'align-middle align-justify',
  {
    [`${textExtraClass}`]: textExtraClass,
  },
);

// istanbul ignore next
const getChildren = ({
  children,
  isOpen,
  textExtraClass,
  openIndicatorExtraClass,
  minusIconSrc,
  plusIconSrc,
  iconWidth,
  iconHeight,
}) => {
  if (typeof children === 'string') {
    const openIndicatorClassName = classNames('accordion__item-icon', openIndicatorExtraClass);

    return (
      <span className={generateAccordionButtonTextClassNames({ textExtraClass })}>
        {children}
        <Icon
          extraClass={openIndicatorClassName}
          iconSrc={generateIconName({ isOpen, minusIconSrc, plusIconSrc })}
          width={iconWidth}
          height={iconHeight}
        />
      </span>
    );
  }

  return React.Children.toArray(children).map(child => React.cloneElement(child, {
    children: getChildren({
      children: child.props.children,
      isOpen,
      textExtraClass,
      openIndicatorExtraClass,
      minusIconSrc,
      plusIconSrc,
    }),
  }));
};

const generateButtonClassNames = ({
  buttonExtraClass,
  openButtonExtraClass,
  isOpen,
}) => classNames(
  buttonExtraClass,
  isOpen && openButtonExtraClass,
);

const generateWrapperClassNames = ({ extraClass }) => (
  classNames(
    'accordion__item-title',
    extraClass,
  )
);

// istanbul ignore next
const AccordionItemTitle = ({
  children,
  id,
  index,
  toggle,
  isOpen,
  extraClass,
  buttonExtraClass,
  openButtonExtraClass,
  textExtraClass,
  openIndicatorExtraClass,
  minusIconSrc,
  plusIconSrc,
  iconWidth,
  iconHeight,
}) => (
  <div className={generateWrapperClassNames({ extraClass })}>
    <Button
      testAttribute="accordion-item-title-button"
      extraClass={generateButtonClassNames({ buttonExtraClass, openButtonExtraClass, isOpen })}
      onClick={() => toggle(id, index)}
      extraProps={
        {
          id: `accordion-item-title-${id}-${index}`,
          role: 'tab',
          'aria-controls': `accordion-item-body-${id}-${index}`,
          'aria-selected': isOpen,
        }
      }
    >
      {
        getChildren({
          children,
          isOpen,
          textExtraClass,
          openIndicatorExtraClass,
          minusIconSrc,
          plusIconSrc,
          iconWidth,
          iconHeight,
        })
      }
    </Button>
  </div>
);

AccordionItemTitle.propTypes = {
  children: PropTypes.node.isRequired,
  id: PropTypes.string,
  index: PropTypes.number,
  isOpen: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
  buttonExtraClass: PropTypes.string,
  openButtonExtraClass: PropTypes.string,
  textExtraClass: PropTypes.string,
  openIndicatorExtraClass: PropTypes.string,
  minusIconSrc: PropTypes.string,
  plusIconSrc: PropTypes.string,
  extraClass: PropTypes.string,
  iconWidth: PropTypes.number,
  iconHeight: PropTypes.number,
};

const mapStateToProps = (state, { id, index }) => ({
  isOpen: R.path(['accordions', id, index, 'isOpen'], state),
});

const mapDispatchToProps = dispatch => ({
  toggle: (id, index) => dispatch(toggleAction(id, index)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AccordionItemTitle);
